/**
 * Calculates optimal CDF granularity for timeseries aggregate queries.
 *
 * Maps time ranges to CDF granularity strings ('1s', '1m', '1h', '1d', '7d', '30d')
 * targeting 300-500 datapoints for smooth chart rendering.
 */

/** CDF-supported granularity values in ascending order of duration. */
export const CDF_GRANULARITIES = ['1s', '1m', '1h', '1d', '7d', '30d'] as const;

export type CdfGranularity = (typeof CDF_GRANULARITIES)[number];

/** Granularity durations in milliseconds. */
const GRANULARITY_MS: Record<CdfGranularity, number> = {
  '1s': 1000,
  '1m': 60 * 1000,
  '1h': 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

/** Target datapoints for chart rendering. */
const TARGET_DATAPOINTS_MIN = 300;
const TARGET_DATAPOINTS_MAX = 500;

/**
 * Calculates the optimal CDF granularity for a time range.
 *
 * Selects the smallest granularity that produces fewer than TARGET_DATAPOINTS_MAX
 * datapoints, ensuring smooth chart rendering without excessive data transfer.
 *
 * @param startMs - Start timestamp in milliseconds
 * @param endMs - End timestamp in milliseconds
 * @returns CDF granularity string ('1s', '1m', '1h', '1d', '7d', '30d') or undefined for raw data
 *
 * @example
 * // 1 hour range - returns undefined (use raw data)
 * calculateGranularity(Date.now() - 3600000, Date.now())
 *
 * // 1 day range - returns '1m' or '1h'
 * calculateGranularity(Date.now() - 86400000, Date.now())
 *
 * // 30 day range - returns '1d'
 * calculateGranularity(Date.now() - 30 * 86400000, Date.now())
 */
export function calculateGranularity(startMs: number, endMs: number): CdfGranularity | undefined {
  const durationMs = Math.abs(endMs - startMs);

  if (durationMs <= 0) {
    return undefined;
  }

  // Find the smallest granularity that keeps datapoints under target
  for (const granularity of CDF_GRANULARITIES) {
    const granularityMs = GRANULARITY_MS[granularity];
    const estimatedDatapoints = durationMs / granularityMs;

    // If this granularity gives us datapoints within our target range, use it
    if (estimatedDatapoints <= TARGET_DATAPOINTS_MAX) {
      // Only use aggregation if it actually reduces the data
      // For very short ranges, raw data is better
      if (estimatedDatapoints < TARGET_DATAPOINTS_MIN) {
        // This granularity is too coarse, check if raw data would work
        // If raw data would give reasonable points, return undefined
        const rawDatapoints = durationMs / 1000; // Assume ~1 point per second worst case
        if (rawDatapoints <= TARGET_DATAPOINTS_MAX) {
          return undefined;
        }
        // Otherwise, this coarse granularity is the best we can do
        return granularity;
      }
      return granularity;
    }
  }

  // If even 30d is too fine, return it anyway (best available)
  return '30d';
}

/**
 * Estimates the number of datapoints for a given time range and granularity.
 *
 * @param startMs - Start timestamp in milliseconds
 * @param endMs - End timestamp in milliseconds
 * @param granularity - CDF granularity string or undefined for raw data estimate
 * @returns Estimated number of datapoints
 */
export function estimateDatapoints(startMs: number, endMs: number, granularity: CdfGranularity | undefined): number {
  const durationMs = Math.abs(endMs - startMs);

  if (granularity === undefined) {
    // Raw data: assume ~1 point per second as conservative estimate
    return Math.ceil(durationMs / 1000);
  }

  return Math.ceil(durationMs / GRANULARITY_MS[granularity]);
}
